import React, {useCallback, useEffect, useState} from "react";
import {Box, CircularProgress, Skeleton, Typography} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import {useHistory, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {DeleteRounded} from "@mui/icons-material";
import {useDispatch} from "react-redux";
import Api from "../../../../../core/services/api/api";
import useIsMounted from "../../../../hooks/use-is-mounted";
import {useConfigureNotebook} from "../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../hooks/use-access-levels";
import Button from "../../../../components/base/button/button";
import {setReduxDialogState} from "../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../core/models/static/routes/redux-store-routes";
import {routes} from "../../../../../core/models/static/routes/app-routes";
import VendorGeneralInfoSection from "./sections/general-info";
import VendorAccountSummarySection from "./sections/account-summary";
import VendorMembersSection from "./sections/members";
import VendorAddressesSection from "./sections/addresses";
import VendorStatisticsSection from "./sections/statistics";

/**
 * Vendor account-info page: profile, AP summary, members, addresses, and stats widgets.
 */
const VendorAccountInfoView = () => {
    const {vendorId} = useParams();
    const history = useHistory();
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.vendor-account-info'});
    const [vendor, setVendor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(false);
    const isMounted = useIsMounted();
    const dispatch = useDispatch();
    const {configureNotebookTags} = useConfigureNotebook('vendor-account-info-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();
    const showStats = operatorAccessLevels.packages.dashboard.vendorInformationStats;

    /**
     * Loads full vendor record for the account-info sections.
     */
    const loadVendor = useCallback(async (load = false) => {
        if (!vendorId) {
            setLoading(false);
            return;
        }
        if (load) {
            setLoading(true);
        }
        const response = await Api.getVendorV2(Number(vendorId));
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setVendor(response.data);
        }
        if (load) {
            setLoading(false);
        }
    }, [isMounted, vendorId]);

    useEffect(() => {
        void loadVendor(true);
    }, [loadVendor]);

    /**
     * Configures notebook tags and behavioural analytics for the vendor account.
     */
    useEffect(() => {
        if (!vendor?.code)
            return;
        configureNotebookTags((tags) => tags.vendorAccountInfo(vendor.code));
        configureBehavioralTool((tags) => tags.vendorAccountInfo(vendor.code));
    }, [configureBehavioralTool, configureNotebookTags, vendor?.code]);

    /**
     * Confirms then soft-deactivates the vendor via CleanAF/Vendor/Deactivate.
     */
    const getRemoveConfirmation = useCallback(() => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: null,
            removedEntryTitle: t('deleteConfirm', {
                name: vendor?.name ?? t('fallbackName'),
            }),
            onClose: async (confirmed) => {
                if (!confirmed || !vendor?.id)
                    return;
                setDeleting(true);
                const response = await Api.deactivateVendor(vendor.id);
                if (!isMounted())
                    return;
                setDeleting(false);
                if (response?.resultFlag) {
                    history.replace(routes.vendors);
                }
                return response?.resultFlag ?? false;
            },
        }));
    }, [dispatch, history, isMounted, t, vendor?.id, vendor?.name]);

    return (
        <>
            <Box sx={{
                display: 'flex',
                marginBlockEnd: 4,
                gap: 2,
                alignItems: 'center',
                justifyContent: 'space-between',
            }}>
                <Typography variant={'h6'} fontWeight={600}>
                    {
                        vendor?.name
                            ? t('title', {name: vendor.name})
                            : t('fallbackName')
                    }
                </Typography>
                {
                    operatorAccessLevels.packages.base.manageVendorAccountInfo &&
                    <Button
                        variant={'outlined'}
                        color={'error'}
                        onClick={getRemoveConfirmation}
                        disabled={loading || deleting || !vendor?.id}
                        startIcon={deleting ? <CircularProgress size={20}/> : <DeleteRounded/>}
                    >
                        {deleting ? t('deletingVendor') : t('deleteVendor')}
                    </Button>
                }
            </Box>
            <Grid2 container spacing={2} sx={{width: '100%'}}>
                <Grid2 xs={12} md={6}>
                    {
                        loading
                            ? <Skeleton sx={{transform: 'none'}} height={'50vh'} width={'100%'}/>
                            : (
                                <VendorGeneralInfoSection
                                    vendor={vendor}
                                    onUpdated={setVendor}
                                    loading={loading}
                                    deleting={deleting}
                                    loadVendor={loadVendor}
                                />
                            )
                    }
                </Grid2>
                <Grid2 xs={12} md={6}>
                    {
                        loading
                            ? <Skeleton sx={{transform: 'none'}} height={'50vh'} width={'100%'}/>
                            : <VendorAccountSummarySection vendorId={vendorId} loading={loading}/>
                    }
                </Grid2>
                <Grid2 xs={12} {...(showStats ? {md: 5, lg: 4, xxl: 3} : {})}>
                    <Grid2 container spacing={2} columns={12}>
                        <Grid2 xs={12}>
                            {
                                loading
                                    ? <Skeleton sx={{transform: 'none'}} height={'40vh'} width={'100%'}/>
                                    : (
                                        <VendorMembersSection
                                            vendorId={vendorId}
                                            loading={loading}
                                            deleting={deleting}
                                        />
                                    )
                            }
                        </Grid2>
                        <Grid2 xs={12}>
                            {
                                loading
                                    ? <Skeleton sx={{transform: 'none'}} height={'40vh'} width={'100%'}/>
                                    : (
                                        <VendorAddressesSection
                                            vendorId={vendorId}
                                            loading={loading}
                                            deleting={deleting}
                                        />
                                    )
                            }
                        </Grid2>
                    </Grid2>
                </Grid2>
                {
                    showStats &&
                    <Grid2 xs={12} md={7} lg={8} xxl={9}>
                        <VendorStatisticsSection
                            loading={loading}
                            vendorCode={vendor?.code}
                            vendorName={vendor?.name}
                        />
                    </Grid2>
                }
            </Grid2>
        </>
    );
};

export default VendorAccountInfoView;
